"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.captureError = exports.RtlJsLogsInstrumentation = exports.LogSeverity = void 0;
const instrumentation_1 = require("@opentelemetry/instrumentation");
const api_1 = require("@opentelemetry/api");
const sdk_trace_web_1 = require("@opentelemetry/sdk-trace-web");
const INSTRUMENTATION_NAME = 'rtl-js-logs';
const STACK_LIMIT = 2048;
const MESSAGE_LIMIT = 1024;
const SPAN_NAME = 'js-log';
var LogSeverity;
(function (LogSeverity) {
    LogSeverity["info"] = "info";
    LogSeverity["warning"] = "warning";
    LogSeverity["error"] = "error";
})(LogSeverity = exports.LogSeverity || (exports.LogSeverity = {}));
var AttributesNames;
(function (AttributesNames) {
    AttributesNames["logMessage"] = "log.message";
    AttributesNames["logObject"] = "log.object";
    AttributesNames["logSeverity"] = "log.severity";
    AttributesNames["logSource"] = "source";
    AttributesNames["logArgument"] = "log.argument";
    AttributesNames["logTargetElement"] = "target_element";
    AttributesNames["logTargetXPath"] = "target_xpath";
    AttributesNames["logTargetSrc"] = "target_src";
    AttributesNames["errorObject"] = "error.object";
    AttributesNames["errorType"] = "error.type";
    AttributesNames["errorStack"] = "error.stack";
})(AttributesNames || (AttributesNames = {}));
class RtlJsLogsInstrumentation extends instrumentation_1.InstrumentationBase {
    constructor(version) {
        super(INSTRUMENTATION_NAME, version, { enabled: true });
    }
    init() { }
    enable() {
        const tracer = api_1.trace.getTracer(INSTRUMENTATION_NAME);
        this.interceptConsoleLogs(tracer);
        window.addEventListener('error', errorListener);
        window.addEventListener('unhandledrejection', unhandledRejectionListener);
    }
    disable() {
        window.removeEventListener('unhandledrejection', unhandledRejectionListener);
        window.removeEventListener('error', errorListener);
    }
    interceptConsoleLogs(tracer) {
        const consoleFns = {
            log: { fn: console.log, severity: LogSeverity.info },
            warn: { fn: console.warn, severity: LogSeverity.warning },
            info: { fn: console.info, severity: LogSeverity.info },
            error: { fn: console.error, severity: LogSeverity.error },
        };
        for (const key in consoleFns) {
            console[key] = function (...args) {
                consoleFns[key].fn.apply(console, args);
                reportConsoleLog(tracer, consoleFns[key].severity, args);
            };
        }
    }
}
exports.RtlJsLogsInstrumentation = RtlJsLogsInstrumentation;
const customTracer = api_1.trace.getTracer(INSTRUMENTATION_NAME);
function errorListener(event) {
    report('onerror', event);
}
function unhandledRejectionListener(event) {
    report('unhandledrejection', event.reason);
}
/**
 * Captures custom errors
 * @param message - The message to show in the metrics. It cannot have high cardinality.
 * @param args - Context information
 */
function captureError(message, args) {
    const span = startJsLogSpan(customTracer, {
        message: message,
        severity: LogSeverity.error,
        source: 'captureError',
    });
    if (args) {
        if (args instanceof Array && args.length > 0) {
            for (let i = 1; i < args.length; i++) {
                span.setAttribute(AttributesNames.logArgument + `_${i}`, getStringFromArg(args[i]));
            }
        }
        else {
            span.setAttribute(AttributesNames.logArgument + '_1', getStringFromArg(args));
        }
    }
    span.end(span.startTime);
}
exports.captureError = captureError;
function getStringFromArg(arg) {
    if (typeof arg === 'string') {
        return arg;
    }
    else if (arg instanceof Error) {
        return arg.message;
    }
    else if (arg instanceof Event) {
        return arg.type;
    }
    else {
        return getJsonStringWithoutPassword(arg);
    }
}
function report(source, arg) {
    if (Array.isArray(arg) && arg.length === 0) {
        return;
    }
    if (arg instanceof Array && arg.length === 1) {
        arg = arg[0];
    }
    if (arg instanceof Error) {
        reportError(source, arg);
    }
    else if (arg instanceof ErrorEvent) {
        reportErrorEvent(source, arg);
    }
    else if (arg instanceof Event) {
        reportEvent(source, arg);
    }
    else if (typeof arg === 'string') {
        reportString(source, arg);
    }
    else if (arg instanceof Array) {
        // if any arguments are Errors then add the stack trace even though the message is handled differently
        const firstError = findFirstError(arg);
        reportString(source, arg.map((x) => getJsonStringWithoutPassword(x)).join(' '), firstError);
    }
    else {
        reportObject(source, arg);
    }
}
function findFirstError(args) {
    for (const arg of args) {
        if (arg instanceof Error) {
            return arg;
        }
    }
    return undefined;
}
function reportErrorEvent(source, ev) {
    if (ev.error) {
        report(source, ev.error);
    }
    else if (ev.message) {
        report(source, ev.message);
    }
}
function reportObject(source, obj) {
    if (!obj) {
        return;
    }
    const span = startJsLogSpan(customTracer, {
        message: obj,
        severity: LogSeverity.error,
        source,
    });
    span.setAttribute(AttributesNames.errorObject, 'Object');
    span.end(span.startTime);
}
function reportString(source, message, firstError) {
    if (!useful(message)) {
        return;
    }
    const span = startJsLogSpan(customTracer, {
        message: message,
        severity: LogSeverity.error,
        source,
        error: firstError,
    });
    span.setAttribute(AttributesNames.errorObject, 'String');
    span.end(span.startTime);
}
function reportError(source, err) {
    const msg = err.message || err.toString();
    if (!useful(msg) && !err.stack) {
        return;
    }
    const span = startJsLogSpan(customTracer, {
        message: msg,
        severity: LogSeverity.error,
        source: source,
        error: err,
    });
    span.setAttribute(AttributesNames.errorObject, useful(err.name) ? err.name : 'Error');
    span.end(span.startTime);
}
function reportEvent(source, ev) {
    if (!ev.target && !useful(ev.type)) {
        return;
    }
    const span = startJsLogSpan(customTracer, {
        message: ev.type,
        severity: LogSeverity.error,
        source,
    });
    span.setAttribute(AttributesNames.errorType, ev.type);
    if (ev.target) {
        span.setAttribute(AttributesNames.logTargetElement, ev.target.tagName);
        span.setAttribute(AttributesNames.logTargetXPath, (0, sdk_trace_web_1.getElementXPath)(ev.target, true));
        span.setAttribute(AttributesNames.logTargetSrc, ev.target.src);
    }
    span.end(span.startTime);
}
function reportConsoleLog(tracer, logSeverity, args) {
    const error = logSeverity !== LogSeverity.info ? new Error() : undefined;
    const span = startJsLogSpan(tracer, {
        message: args[0],
        severity: logSeverity,
        source: 'console',
        error,
    });
    if (args.length > 1) {
        for (let i = 1; i < args.length; i++) {
            span.setAttribute(AttributesNames.logArgument + `_${i}`, args[i]);
        }
    }
    span.end(span.startTime);
}
function startJsLogSpan(tracer, jsLogSpan) {
    const span = tracer.startSpan(SPAN_NAME);
    if (jsLogSpan.severity !== LogSeverity.info) {
        span.setStatus({ code: api_1.SpanStatusCode.ERROR });
        if (jsLogSpan.error) {
            addStackIfUseful(span, jsLogSpan.error);
        }
    }
    span.setAttribute(AttributesNames.logSeverity, jsLogSpan.severity);
    span.setAttribute(AttributesNames.logSource, jsLogSpan.source);
    registerMessage(span, jsLogSpan.message);
    return span;
}
function registerMessage(span, obj) {
    let message;
    let registerObject = true;
    if (typeof obj === 'string') {
        message = obj;
        registerObject = false;
    }
    else if (typeof obj.message === 'string') {
        message = obj.message;
    }
    else if (typeof obj.type === 'string') {
        message = obj.type;
    }
    else {
        message = getJsonStringWithoutPassword(obj);
        registerObject = false;
    }
    span.setAttribute(AttributesNames.logMessage, limitLen(sanatizeMessage(message), MESSAGE_LIMIT));
    if (registerObject) {
        span.setAttribute(AttributesNames.logObject, getJsonStringWithoutPassword(obj));
    }
}
function sanatizeMessage(message) {
    // Replace all " with ' to fix grafana Data Links
    message = replaceAll(message, '"', "'");
    // TODO: Fix this message from the source. Messages shouldn't contains timestamps
    const mediaHeartBeatIndex = message.lastIndexOf('[MediaHeartbeat]');
    if (mediaHeartBeatIndex > -1) {
        return message.substring(mediaHeartBeatIndex);
    }
    return message;
}
function replaceAll(str, find, replace) {
    return str.replace(new RegExp(find, 'g'), replace);
}
function useful(s) {
    return s && s.trim() !== '' && !s.startsWith('[object') && s !== 'error';
}
function addStackIfUseful(span, err) {
    if (err && err.stack && useful(err.stack)) {
        span.setAttribute(AttributesNames.errorStack, limitLen(err.stack.toString(), STACK_LIMIT));
    }
}
function limitLen(s, cap) {
    if (s.length > cap) {
        return s.substring(0, cap);
    }
    else {
        return s;
    }
}
function hidePasswords(object) {
    const key = 'password';
    Object.keys(object).some(function (propertyName) {
        if (propertyName === key) {
            object[propertyName] = '*******';
        }
        if (object[propertyName] && typeof object[propertyName] === 'object') {
            hidePasswords(object[propertyName]);
        }
    });
    return object;
}
function getJsonStringWithoutPassword(original) {
    return JSON.stringify(hidePasswords(JSON.parse(JSON.stringify(original))));
}
